import moment from "moment";
import { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import { concatParams, CreateParamsJson } from "../../helper/pathHelper";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";

const format = "YYYY-MM-DDTHH:mm:ss";

export default function StarttimePicker({ path, config }) {
  const json = CreateParamsJson();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // The field's displayed value is local state, not the URL, while the
  // user is editing it. datetime-local inputs are edited segment by
  // segment (month, day, year, hour, ...); if the value we feed back in
  // is re-derived from the URL on every keystroke, the round trip through
  // navigate() and back resets which segment the picker considers active,
  // so later keystrokes land on the wrong segment and the edit gets
  // silently mangled. lastPushed lets an external change to the URL
  // (Restart, Reset, ...) still update the field, while our own
  // navigate() calls don't bounce back and clobber what's being typed.
  const lastPushed = useRef(null);
  const [value, setValue] = useState(() =>
    json.starttime ? moment(json.starttime).format(format) : ""
  );

  useEffect(() => {
    if (json.starttime && json.starttime !== lastPushed.current) {
      setValue(moment(json.starttime).format(format));
    }
  }, [json.starttime]);

  if (!json.starttime) {
    return null;
  }

  return (
    <form noValidate>
      <TextField
        variant="standard"
        className="settimer-field"
        id="datetime-local"
        key={config}
        label={t("timer.starttime")}
        type="datetime-local"
        value={value}
        onChange={(event) => {
          const raw = event.target.value;
          setValue(raw);

          // Parse without a fixed format: some browsers (notably iOS
          // Safari) omit seconds from a datetime-local value even with
          // step=1, and moment(value, "...:ss") can then fail to parse,
          // silently dropping the new start time. moment(value) reads
          // any ISO-8601-shaped string - with or without seconds - as
          // local time, which is exactly what datetime-local means.
          const parsed = moment(raw);
          if (!parsed.isValid()) {
            return;
          }
          const iso = parsed.toISOString();
          lastPushed.current = iso;
          navigate({
            pathname: path,
            search: concatParams({
              ...json,
              starttime: iso,
            }),
          });
        }}
        slotProps={{
          htmlInput: {
            step: 1, // 5 min
          },
        }}
      />
    </form>
  );
}
